import { formatSpeakerDisplayLabel } from '@app/modules/diarization/speaker-utils';
import { Chip } from '@mui/material';
import { usePerson } from '../hooks/usePerson';
import { PersonAvatar } from './PersonAvatar';

type SpeakerDisplayProps = {
  /** When set, resolves person name / avatar via Person API cache. */
  personId?: string;
  /** voice_label-based label used when person is missing or the fetch fails. */
  fallbackLabel: string;
  /** Optional speaker color used as the chip background. */
  color?: string;
  size?: 'small' | 'medium' | 'large';
};

/**
 * Shared speaker chip: MUI Chip with PersonAvatar + name.
 * With personId: shows avatar (Person icon while loading, then image/initials).
 * Without personId: formatted voice_label only, no avatar.
 */
export function SpeakerDisplay({
  personId,
  fallbackLabel,
  color,
  size = 'medium',
}: SpeakerDisplayProps) {
  const person = usePerson(personId);
  const displayName = person?.name ?? formatSpeakerDisplayLabel(fallbackLabel);
  /** Keep the avatar slot while personId is set so loading does not flash a bare chip. */
  const showAvatar = !!personId;
  /** Chip only supports small | medium; map large to medium and size the avatar separately. */
  const chipSize = size === 'large' ? 'medium' : size;

  return (
    <Chip
      size={chipSize}
      avatar={
        showAvatar ? (
          // No fixed size: Chip applies avatar margin + 18/24px (MUI avatar chip pattern).
          // Without `person` yet, PersonAvatar renders the default Person icon.
          <PersonAvatar person={person} fallbackName={displayName} />
        ) : undefined
      }
      label={displayName}
      sx={{
        maxWidth: '100%',
        ...(color ? { bgcolor: color } : null),
      }}
    />
  );
}
